/**
 * Image Cache Utility
 * Provides in-memory caching for images to improve loading performance
 */

class ImageCache {
  constructor() {
    this.cache = new Map();
    this.loadingPromises = new Map();
  }

  /**
   * Get image from cache or load it
   * @param {string} url - Image URL
   * @returns {Promise<string>} - Image blob URL
   */
  async get(url) {
    // Return from cache if available
    if (this.cache.has(url)) {
      return this.cache.get(url);
    }

    // Return existing loading promise if image is being fetched
    if (this.loadingPromises.has(url)) {
      return this.loadingPromises.get(url);
    }

    // Create new loading promise
    const loadPromise = this.loadImage(url);
    this.loadingPromises.set(url, loadPromise);

    try {
      const blobUrl = await loadPromise;
      this.cache.set(url, blobUrl);
      return blobUrl;
    } finally {
      this.loadingPromises.delete(url);
    }
  }

  /**
   * Load image and convert to blob URL
   * @param {string} url - Image URL
   * @returns {Promise<string|null>} - Blob URL or null if not found
   */
  async loadImage(url) {
    const token = localStorage.getItem('token');
    const headers = {};

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(url, { headers });

    // Don't cache 404s - allow retry in case photo was fixed
    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }
      throw new Error(`Failed to load image: ${response.statusText}`);
    }

    const blob = await response.blob();
    return URL.createObjectURL(blob);
  }

  /**
   * Preload multiple images in parallel
   * @param {string[]} urls - Array of image URLs
   * @param {number} batchSize - Number of images to load in parallel
   * @returns {Promise<void>}
   */
  async preload(urls, batchSize = 5) {
    const batches = [];
    for (let i = 0; i < urls.length; i += batchSize) {
      batches.push(urls.slice(i, i + batchSize));
    }

    for (const batch of batches) {
      await Promise.all(
        batch.map(url => this.get(url).catch(err => {
          console.warn(`Failed to preload image ${url}:`, err);
          return null;
        }))
      );
    }
  }

  /**
   * Clear specific image from cache
   * @param {string} url - Image URL
   */
  clear(url) {
    const blobUrl = this.cache.get(url);
    if (blobUrl) {
      URL.revokeObjectURL(blobUrl);
      this.cache.delete(url);
    }
  }

  /**
   * Clear all cached images
   */
  clearAll() {
    this.cache.forEach(blobUrl => URL.revokeObjectURL(blobUrl));
    this.cache.clear();
    this.loadingPromises.clear();
  }

  /**
   * Get cache size
   * @returns {number}
   */
  size() {
    return this.cache.size;
  }
}

// Export singleton instance
const imageCache = new ImageCache();
export default imageCache;
